import { useRef } from 'react';
import type { RefObject } from 'react';

import useFocusAfterRemoval from 'core/hooks/useFocusAfterRemoval';
import { getMainContent } from 'core/utils/focusTargets';

import type { License } from './LicensesList';

// Contrato con LicenseItem: si su botón de quitar cambia de clase, el foco deja de recolocarse al borrar
const DELETE_BUTTON_SELECTOR = '.license-item__content-delete';

type Params = {
  onClickView: (license: License) => void;
  onDelete?: (license: License) => void;
};

type UseLicensesList = {
  getClickViewHandler: (license: License) => () => void;
  getDeleteHandler: (license: License, index: number) => (() => void) | null;
  listRef: RefObject<HTMLDivElement>;
};

function useLicensesList({ onClickView, onDelete }: Params): UseLicensesList {
  const listRef = useRef<HTMLDivElement>(null);
  const { keepFocusAfterRemoval } = useFocusAfterRemoval();

  function getDeleteButtons(): HTMLElement[] {
    return Array.from(listRef.current?.querySelectorAll<HTMLElement>(DELETE_BUTTON_SELECTOR) ?? []);
  }

  function getClickViewHandler(license: License): () => void {
    return function handleClickView(): void {
      onClickView(license);
    };
  }

  function getDeleteHandler(license: License, index: number): (() => void) | null {
    if (!onDelete) return null;

    return function handleDelete(): void {
      // Se toman antes de borrar: una licencia puede arrastrar varios programas con el mismo código
      const buttons = getDeleteButtons();
      keepFocusAfterRemoval({
        getTargets: () => [...buttons.slice(index + 1), ...buttons.slice(0, index).reverse(), getMainContent()],
        hasBeenRemoved: () => getDeleteButtons().length < buttons.length,
      });
      onDelete(license);
    };
  }

  return { getClickViewHandler, getDeleteHandler, listRef };
}

export default useLicensesList;
